//* Libraries imports
import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Container, Row, Col, Button } from "react-bootstrap";

//* Hooks imports
import { useGetEnrollmentProcessById } from "../../../../hooks/queries/enrollment/use-get-enrollment-process";

//* Components imports
import { EnrollmentHeader } from "./enrollment-header";
import { EnrollmentInfo } from "./enrollment-info";
import { EnrollmentDocuments } from "./enrollment-documents";
import { ErrorMessages } from "../../../../component/common/ErrorMessages";
import { ViewHeaderProcess } from "../../../ProcessView/view/_components/view-header-process";

//* Icons imports
import { BsArrowLeft } from "react-icons/bs";
import Icon from "../../../../components/common/Icon";

//* Styles imports
import "./content.scss";

export function Content() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const idNumber = useMemo(() => {
    if (!id) return null;
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? null : parsed;
  }, [id]);

  const {
    data: enrollmentResponse,
    isPending,
    isError,
  } = useGetEnrollmentProcessById(idNumber);

  const enrollment = useMemo(() => {
    return enrollmentResponse?.data || null;
  }, [enrollmentResponse]);

  const handleBack = () => {
    navigate(-1);
  };

  // Show loading state
  if (isPending) {
    return (
      <motion.div
        className="d-flex justify-content-center align-items-center min-vh-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted">Carregando informações da inscrição...</p>
        </div>
      </motion.div>
    );
  }

  // Show error for invalid ID
  if (!idNumber) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            "ID da inscrição inválido. Verifique o link e tente novamente.",
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  if (isError || !enrollmentResponse?.success || !enrollment) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            enrollmentResponse?.message ||
              "Não foi possível carregar a inscrição. O processo de inscrição pode ter sido diferente ou houve um erro. Por favor, tente novamente ou entre em contato com o suporte.",
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  return (
    <motion.div
      className="enrollment-view-container min-vh-100 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="d-flex flex-column h-100">
        <motion.section
          className="pt-4 p-2 view-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ borderRadius: "15px" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 enrollment-details-header px-3">
            <div className="d-flex align-items-center">
              <Button
                variant="light"
                className="me-3 rounded-circle p-2 lh-1 back-button d-flex align-items-center justify-content-center"
                onClick={handleBack}
                style={{ width: "40px", height: "40px" }}
              >
                <Icon icon={BsArrowLeft} size={18} className="icon" />
              </Button>
              <div>
                <motion.h2
                  className="fs-1 fw-bold mb-0 enrollment-title"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  {enrollment.nomeCompleto}
                </motion.h2>
                <p className="text-muted small mb-0 enrollment-subtitle">
                  Detalhes da Inscrição
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Main Content */}
        <Container fluid className="flex-grow-1 py-4">
          <Row className="justify-content-center">
            <Col xxl={10} xl={11} lg={12} style={{width:'100%',padding:0}}>
              {/* Status and Schedule Highlight */}
              <EnrollmentHeader enrollment={enrollment} />

              {/* Personal and Academic Information */}
              <EnrollmentInfo enrollment={enrollment} />

              {/* Documents */}
              <EnrollmentDocuments enrollment={enrollment} />
            </Col>
          </Row>
        </Container>
      </div>
    </motion.div>
  );
}
